import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { UserIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import AuthContext from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import FollowList from '../components/follow/FollowList';
import {
  FollowUser,
  followUser,
  unfollowUser
} from '../services/followService';
import { collection, query, orderBy, getDocs, limit, startAfter } from 'firebase/firestore';
import { db } from '../firebase/config';

const ExplorePeoplePage: React.FC = () => {
  const { t: _t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState<FollowUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastVisible, setLastVisible] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentUserFollowing, setCurrentUserFollowing] = useState<string[]>([]);

  const fetchUserFollowing = async () => {
    if (!currentUser) return [];

    try {
      // Obtener la lista de usuarios que el usuario actual sigue
      const followingCollectionRef = collection(db, `users/${currentUser.uid}/following`);
      const followingSnapshot = await getDocs(followingCollectionRef);
      const followingIds = followingSnapshot.docs.map(doc => doc.id);
      setCurrentUserFollowing(followingIds);
      return followingIds;
    } catch (error) {
      console.error('Error fetching user following:', error);
      return [];
    }
  };

  const fetchUsers = async (isInitialLoad = true) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      // Obtener la lista de usuarios que el usuario actual sigue
      const followingIds = isInitialLoad ? await fetchUserFollowing() : currentUserFollowing;

      // Consulta para obtener usuarios
      let usersQuery;

      if (isInitialLoad) {
        usersQuery = query(
          collection(db, 'users'),
          orderBy('lastActive', 'desc'),
          limit(20)
        );
      } else if (lastVisible) {
        usersQuery = query(
          collection(db, 'users'),
          orderBy('lastActive', 'desc'),
          startAfter(lastVisible),
          limit(20)
        );
      } else {
        setLoadingMore(false);
        return;
      }

      const querySnapshot = await getDocs(usersQuery);

      // Actualizar el último documento visible para paginación
      if (querySnapshot.docs.length > 0) {
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
        setHasMore(querySnapshot.docs.length === 20);
      } else {
        setHasMore(false);
      }

      const fetchedUsers: FollowUser[] = [];

      querySnapshot.forEach((doc) => {
        // No incluir al usuario actual
        if (currentUser && doc.id !== currentUser.uid) {
          const userData = doc.data() as any;
          fetchedUsers.push({
            uid: doc.id,
            displayName: userData.displayName || 'Usuario',
            photoURL: userData.photoURL || '/default-avatar.png',
            bio: userData.bio,
            isFollowing: followingIds.includes(doc.id)
          });
        }
      });

      if (isInitialLoad) {
        setUsers(fetchedUsers);
      } else {
        setUsers(prevUsers => [...prevUsers, ...fetchedUsers]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Error al cargar usuarios. Inténtalo de nuevo más tarde.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchUsers();
    } else {
      setUsers([]);
      setLoading(false);
    }
  }, [currentUser]);

  const handleFollowToggle = async (targetUserId: string) => {
    if (!currentUser) return;

    try {
      const userToUpdate = users.find(user => user.uid === targetUserId);

      if (!userToUpdate) {
        console.error('Usuario no encontrado');
        return;
      }

      // Guardar el estado actual de seguimiento
      const isCurrentlyFollowing = userToUpdate.isFollowing;

      // Actualizar estado local usando el patrón funcional
      // Esto evita problemas de sincronización con el DOM virtual de React
      setUsers(prevUsers => {
        return prevUsers.map(user =>
          user.uid === targetUserId
            ? { ...user, isFollowing: !isCurrentlyFollowing }
            : user
        );
      });

      // Actualizar la lista de seguimiento del usuario actual usando el patrón funcional
      if (isCurrentlyFollowing) {
        setCurrentUserFollowing(prev => prev.filter(id => id !== targetUserId));
      } else {
        setCurrentUserFollowing(prev => [...prev, targetUserId]);
      }

      // Realizar la operación en la base de datos
      if (isCurrentlyFollowing) {
        await unfollowUser(currentUser.uid, targetUserId);
      } else {
        await followUser(
          currentUser.uid,
          targetUserId,
          {
            displayName: currentUser.displayName || 'Usuario',
            photoURL: currentUser.photoURL
          },
          {
            displayName: userToUpdate.displayName,
            photoURL: userToUpdate.photoURL
          }
        );
      }
    } catch (error) {
      console.error('Error al cambiar estado de seguimiento:', error);
      setError('Error al actualizar estado de seguimiento. Inténtalo de nuevo.');

      // En caso de error, revertir los cambios locales
      fetchUsers();
    }
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      fetchUsers(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden p-4">
      <div className="flex items-center mb-6">
        <Link
          to="/"
          className="mr-2 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          aria-label="Volver"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        </Link>
        <div className="flex items-center">
          <UserIcon className="h-6 w-6 mr-2 text-domired-600 dark:text-domired-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Explorar personas</h1>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 p-3 rounded-md mb-4">
          {error}
        </div>
      )}

      <FollowList
        users={users}
        loading={loading}
        emptyMessage="No hay usuarios disponibles en este momento."
        onFollowToggle={handleFollowToggle}
      />

      {hasMore && (
        <div className="py-4 text-center">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500 disabled:opacity-50"
          >
            {loadingMore ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Cargando más usuarios...
              </span>
            ) : 'Cargar más usuarios'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ExplorePeoplePage;
